'use client'

import React, { FC, useCallback, useSyncExternalStore } from 'react'
import classNames from 'classnames'
import { Box, Square } from 'lucide-react'

/**
 * The viewer's 2D/3D preference for live bitmaps, kept in a cookie (as
 * bitmap.trade does) and shared by every tile on the page. 3D is the default:
 * only a viewer who has switched to 2D gets the flat view.
 */
const COOKIE = 'bitmap-3d-enabled'
const listeners = new Set<() => void>()

function read(): boolean {
	if (typeof document === 'undefined') return true
	const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`))
	return !m || m[1] !== '0'
}

function write(enabled: boolean) {
	const expires = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString()
	document.cookie = `${COOKIE}=${enabled ? '1' : '0'}; expires=${expires}; path=/; SameSite=Lax`
	for (const l of listeners) l()
}

function subscribe(onChange: () => void) {
	listeners.add(onChange)
	return () => {
		listeners.delete(onChange)
	}
}

export function useBitmap3d(): [boolean, () => void] {
	const enabled = useSyncExternalStore(subscribe, read, () => true)
	const toggle = useCallback(() => write(!read()), [])
	return [enabled, toggle]
}

/**
 * The 2D/3D switch. `prominent` is the inline collection control.
 * Compact item controls sit in the corner of their artwork; floating remains
 * available for surfaces that need a viewport control.
 */
export const Bitmap3dToggle: FC<{
	floating?: boolean
	prominent?: boolean
	className?: string
}> = ({ floating, prominent, className }) => {
	const [enabled, toggle] = useBitmap3d()
	const label = enabled ? 'Switch to 2D view' : 'Switch to 3D view'
	return (
		<button
			type="button"
			onClick={(e) => {
				e.stopPropagation()
				toggle()
			}}
			aria-label={label}
			title={label}
			aria-pressed={enabled}
			className={classNames(
				'flex items-center justify-center rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
				prominent
					? 'border-0 bg-transparent text-[#6941C6] shadow-none hover:bg-muted/50 dark:text-[#B692F6]'
					: 'border border-white/15 shadow-lg backdrop-blur-sm bg-[rgba(13,17,23,0.9)] text-white/70 hover:border-[rgba(158,119,237,0.6)] hover:text-white',
				enabled &&
					!prominent &&
					'border-[rgba(158,119,237,0.7)] bg-[rgba(158,119,237,0.2)] text-[#B692F6] shadow-[0_0_12px_rgba(158,119,237,0.25)]',
				prominent
					? 'relative size-12'
					: floating
					? 'fixed bottom-12 right-4 z-40 size-10'
					: 'absolute bottom-3 right-3 z-10 size-9',
				className
			)}
		>
			{enabled ? (
				<Square className={prominent ? 'size-6' : 'size-4'} />
			) : (
				<Box className={prominent ? 'size-6' : 'size-4'} />
			)}
		</button>
	)
}
